import React from 'react';
import { connect, ConnectedProps } from '@cerebral/react';
import { state } from 'app/pwa/app.cerebral';

import { getIndicatorConfig, getIndicator } from 'utils/common';
import { mainText, subText } from 'utils/translate';
import LazyImage from 'common/components/LazyImage';
import TranslateContainer from 'challenge/common/TranslateContainer';
import { INDICATOR } from 'resources/constants';

import Lab115B from 'dc/Lab115B';
import SpaHR01 from 'dc/SpaHR01';
import Spa16 from 'dc/Spa16';
import ChStPrt4 from 'dc/ChStPrt4';
import {
  Wrapper,
  Header,
  NumberWrapper,
  StatsContainer,
  TranslateWrapper,
  ListContainer,
  SectionList,
} from './elements';

import {
  useText,
  useFormatIndicator,
  useConfig,
  useGetAvatarUrl,
} from 'common/hooks';

type Props = {
  readonly indicator: string;
};

const deps = {
  totalTeamStats:
    state.challenge.stats.total.teams[state.challenge.userCampaign.teamKey],
  totalSectionStats:
    state.challenge.stats.total.sections[
      state.challenge.userCampaign.sectionKey
    ],
  totalUserStats:
    state.challenge.stats.total.profiles[state.appContainer.user.uid],
  totalChallengeStats: state.challenge.stats.total.challenge,
  profile: state.appContainer.profiles[state.appContainer.user.uid],
  sectionStats: state.challenge.stats.total.sections,
  sections: state.challenge.campaign.structure.sections,
  translate: state.challenge.visualisations.translates.insightsAverage,
  joinedSectionKey: state.challenge.userCampaign.sectionKey,
  organisationName: state.challenge.campaign.organisation.name,
  sectionTerm: state.challenge.campaign.sectionTerm,
  specifiedTerm: state.challenge.campaign.specifiedTerm,
};

export function AverageInsights({
  totalTeamStats,
  totalSectionStats,
  totalUserStats,
  totalChallengeStats,
  profile,
  translate,
  sectionStats,
  sections,
  joinedSectionKey,
  organisationName,
  sectionTerm,
  specifiedTerm,
  indicator,
}: Props & typeof deps & ConnectedProps): React.ReactElement {
  const t = useText();
  const formatIndicator = useFormatIndicator();
  const { staticUrl, translateImagePath } = useConfig();
  const getAvatarUrl = useGetAvatarUrl();
  const { icon, color, unitPath } = getIndicatorConfig(indicator);

  const totalAverageIndicator = getIndicator(
    totalChallengeStats,
    indicator,
    'average'
  );

  const totalUserIndicator = getIndicator(totalUserStats, indicator);

  const averageTeamIndicator = getIndicator(
    totalTeamStats,
    indicator,
    'average'
  );

  const averageSectionIndicator = getIndicator(
    totalSectionStats,
    indicator,
    'average'
  );

  return (
    <Wrapper>
      <Header
        title={t('challenge.ChaPaD24B.ChaStat2BD.participant_savings', {
          organisationName,
        })}
      />
      <NumberWrapper>
        <Lab115B
          icon={icon}
          color={color}
          label={t(unitPath)}
          value={formatIndicator(totalAverageIndicator, indicator)}
        />
      </NumberWrapper>
      <StatsContainer>
        {indicator !== INDICATOR.points && (
          <TranslateWrapper>
            <TranslateContainer
              placeholder={!translate}
              placeholderText={t('challenge.ChaPaD24B.ChaStat2BD.PlaHo01')}
              translateImageUrl={staticUrl + translateImagePath}
              mainText={mainText(t, translate?.data)}
              subText={subText(t, translate?.data)}
              translate={{
                ...translate?.data,
                units:
                  translate?.data?.units &&
                  t(`translate.units.${translate?.data?.units}`),
              }}
            />
          </TranslateWrapper>
        )}
        <ListContainer $points={indicator === INDICATOR.points}>
          <LazyImage
            path={getAvatarUrl(profile).small}
            onLoading={() => (
              <ChStPrt4
                you_text={t('challenge.ChStPrt4.you')}
                average_in_team_text={t('challenge.ChStPrt4.average_in_team')}
                average_in_unit_text={t(
                  'challenge.ChStPrt4.average_in_section_term.section'
                )}
                sectionTerm={sectionTerm}
                isLoadingAvatar
              />
            )}
            onLoaded={imageUrl => (
              <ChStPrt4
                you_text={t('challenge.ChStPrt4.you')}
                average_in_team_text={t('challenge.ChStPrt4.average_in_team')}
                average_in_unit_text={
                  sectionTerm === 'other'
                    ? specifiedTerm
                    : t(
                        `challenge.ChStPrt4.average_in_section_term.${sectionTerm}`
                      )
                }
                sectionTerm={sectionTerm}
                avatarImage={imageUrl}
                profilePercentage={
                  (totalUserIndicator / totalAverageIndicator) * 100
                }
                profileIndicator={formatIndicator(
                  totalUserIndicator,
                  indicator
                )}
                teamPercentage={
                  (averageTeamIndicator / totalAverageIndicator) * 100
                }
                teamIndicator={formatIndicator(averageTeamIndicator, indicator)}
                sectionPercentage={
                  (averageSectionIndicator / totalAverageIndicator) * 100
                }
                sectionIndicator={formatIndicator(
                  averageSectionIndicator,
                  indicator
                )}
                indicatorIcon={icon}
                indicatorColor={color}
              />
            )}
          />
          {Object.keys(sections).length > 1 && (
            <>
              <SpaHR01 />
              <SectionList
                indicator={indicator}
                joinedSectionKey={joinedSectionKey}
                sections={sections}
                sectionStats={sectionStats}
                totalAverageIndicator={totalAverageIndicator}
                indicatorIcon={icon}
                progressColor={color}
              />
            </>
          )}
        </ListContainer>
      </StatsContainer>
      <Spa16 />
    </Wrapper>
  );
}

export default connect<Props, typeof deps>(deps, AverageInsights);
